import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';

export interface SummaryItem {
  /** The field's element id; the label links to it. Absent for a message no field owns. */
  id?: string;
  label?: string;
  message: string;
}

/**
 * "Not saved: fix 2 fields", above the form, after a save that found errors.
 *
 * Each label is a link to its field, so a form taller than the screen can be fixed
 * without hunting for the red. It is an alert so a screen reader hears why nothing
 * happened; focus itself goes to the first wrong field, where the fixing starts.
 */
const ErrorSummary: React.FC<{ items: SummaryItem[]; radius?: number | string }> = ({ items, radius = 4 }) => {
  const { t } = useTranslation();
  if (!items.length) return null;
  const fieldCount = items.filter(item => item.id).length;
  return (
    <Box role="alert" sx={{ border: 1, borderColor: 'error.main', borderRadius: radius, bgcolor: 'tint.error', p: '16px 20px', mb: 3 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
        <ErrorOutlineIcon aria-hidden sx={{ fontSize: 18 }} />
        {fieldCount ? t('validation.summary.title', { count: fieldCount }) : t('validation.summary.notSaved')}
      </Typography>
      <Box component="ul" sx={{ m: 0, mt: 1, pl: 3.5, fontSize: 14 }}>
        {items.map((item, index) => (
          <li key={item.id ?? index}>
            {item.id && item.label ? (
              <>
                <Link
                  href={`#${item.id}`}
                  onClick={event => {
                    event.preventDefault();
                    const element = document.getElementById(item.id!);
                    element?.focus();
                    element?.scrollIntoView?.({ block: 'center' });
                  }}
                  sx={{ color: 'error.main', fontWeight: 500 }}
                >
                  {item.label}
                </Link>
                : {item.message}
              </>
            ) : (
              item.message
            )}
          </li>
        ))}
      </Box>
    </Box>
  );
};

export default ErrorSummary;
